/**
 * Top-risks hook for the home-page widget — fetches unresolved risks
 * (backend already filters resolved by default), then ranks by severity
 * and trims to N.
 */
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "@/api/client";
import { listRisks, type RiskIndicator, type Severity } from "@/api/risks";

export const TOP_RISKS_KEY = ["risks", "top"] as const;

const SEVERITY_RANK: Record<Severity, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

export interface UseTopRisks {
  risks: RiskIndicator[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

export function useTopRisks(limit = 5): UseTopRisks {
  const query = useQuery<RiskIndicator[], Error>({
    queryKey: TOP_RISKS_KEY,
    queryFn: () => listRisks({ includeResolved: false }),
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 401) return false;
      return failureCount < 1;
    },
    staleTime: 30_000,
  });
  const risks = (query.data ?? [])
    .slice()
    .sort((a, b) => {
      const sev = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
      if (sev !== 0) return sev;
      return b.detected_at.localeCompare(a.detected_at);
    })
    .slice(0, limit);
  return { risks, isLoading: query.isLoading, isError: Boolean(query.error), error: query.error };
}
